// Angular library
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit
} from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';

// Local library
import {
  CardDatePipe,
  CardNamePipe,
  CardNumEndPipe,
  IsEmptyPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import { IDebitCreditCard, WithdrawDebitCreditCardService } from '@amwager/funding/data-access';

// Third party
import * as _ from 'lodash';
import { Observable, Subscription, of, takeUntil } from 'rxjs';

@Component({
  selector: 'feature-mat-withdraw-debit-credit-card',
  templateUrl: './debit-credit-card.component.html',
  styleUrls: ['./debit-credit-card.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IsEmptyPipe,
    CardNamePipe,
    CardDatePipe,
    CardNumEndPipe,
    TranslatePipe
  ],
  standalone: true,
  providers: [WithdrawDebitCreditCardService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DebitCreditCardComponent
  extends UnsubscribeObservableService implements OnInit {

  private subsGetCardResponse!: Subscription;
  private tmsId?: string;
  private subsWithdrawCardResponse!: Subscription;

  showAddCard: boolean = false;
  debitAndCreditCardForm!: FormGroup;

  // convenience getter for easy access to form fields
  get formVal() { return this.debitAndCreditCardForm?.controls }

  showCardList = false;
  orderId?: string;
  cards$!: Observable<IDebitCreditCard[]>;
  amount!: number;
  total!: number;
  isWithDrawing = false;
  cardContentHTMList!: HTMLElement[];

  isOnline: boolean = true;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly debitCreditCardService: WithdrawDebitCreditCardService,
  ) {
    super();
  }

  ngOnInit() {
    this.checkConnectivity();
    this.observerTmsIds();

    // OLD (moved to WithdrawDebitCreditCardService.buildForm):
    // this.initForm();
    this.debitAndCreditCardForm = this.debitCreditCardService.buildForm();

    this.total = this.amount;

    // OLD (getDebitCreditCard + list stream moved to WithdrawDebitCreditCardService.loadCards$):
    // this.fundingFacade.getDebitCreditCard();
    // this.subsGetCardResponse = this.fundingFacade.getListCards$()
    this.subsGetCardResponse = this.debitCreditCardService.loadCards$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((response) => {
      if (response !== undefined) {
        const cards = response.filter((card: { Type: string; }) => card.Type === 'VI');
        this.cards$ = of(cards);
        this.showCardList = true;

        // set default card
        setTimeout(()=>{
          this.cardContentHTMList = this.elementRef.nativeElement.querySelectorAll(
            '.section-card-content .card-list-container .card-per-row'
          );

          _.forEach(this.cardContentHTMList, (html: HTMLElement, index: number) => {
            if (index === 0) {
              this.orderId = cards[0]?.OrderId || 0;
              html.classList.add('active');
            }
          });
        }, 200);
      }
      this.cdRef.markForCheck();
    });

  }

  private observerTmsIds(): void {
    // OLD (moved to WithdrawDebitCreditCardService.tmsId$):
    // this.commonFacade?.tmsId$()
    this.debitCreditCardService.tmsId$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((tmsId: string | undefined) => {
        this.tmsId = tmsId;
      });
  }

  chooseAmount(amount: number): void {
    this.formVal['amount']?.setValue(amount);
    this.amount = amount;

    this.total = this.amount;
  }

  showAlertMessage(header: string, message: string): void {
    // OLD (moved to WithdrawDebitCreditCardService.showAlert):
    // this.matNotificationService.showAlert(header, message, undefined, undefined, 'Close');
    this.debitCreditCardService.showAlert(header, message);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chooseCard($event: any, method: string, card: string, orderId: string): void {

    const cardRows = this.elementRef.nativeElement.querySelectorAll(
      '.card-per-row'
    );
    cardRows.forEach((cardRow: HTMLElement) => {
      cardRow.classList.remove('active');
    });

    const creditCard = this.elementRef.nativeElement.querySelector(
      '.card-content .card-list-container #'
      + card
      + '.card-per-row'
    );

    this.total = this.amount;

      if (creditCard.classList.contains('active')) {
        creditCard.classList.remove('active');
        this.orderId = '';

        this.total = this.amount;
      }
      else {
        creditCard.classList.add('active');
        this.orderId = orderId;
      }
  }

  withdrawAmount(): void {
    this.amount = this.formVal['amount']?.value;
    this.total = this.amount;
  }

  withdraw(): void {
    if (!this.isOnline) {
      // OLD (moved to WithdrawDebitCreditCardService.setOffline):
      // this.commonFacade.setIsConnectedInternet('DebitCreditCardComponent:withdraw', false);
      this.debitCreditCardService.setOffline('DebitCreditCardComponent:withdraw');
      return;
    }

    if (this.debitAndCreditCardForm.valid && this.amount && this.orderId && this.tmsId) {
      this.isWithDrawing = true;

      // OLD (dispatch + success stream moved to WithdrawDebitCreditCardService.withdraw$):
      // this.fundingFacade.withdrawDebitCreditCard(this.amount, this.orderId, this.tmsId);
      // this.subsWithdrawCardResponse = this.fundingFacade.isWithdrawDebitCreditCardSuccess$()
      this.subsWithdrawCardResponse = this.debitCreditCardService.withdraw$(this.amount, this.orderId, this.tmsId)
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(({response, isSuccess}) => {
        if (!isSuccess && response) {

          // Temporary update balance if specific error meet.
          if (
            response.error.Error
              ===
            'Unknown Error - Please retry, if you receive this error multiple times please contact support.'
          ) {
            this.showAlertMessage('Successful Withdrawal', 'Successfully Withdraw Amount');

            // OLD (moved to WithdrawDebitCreditCardService.refreshBalance):
            // this.accountFacade.getBalance("WagerEffects:withdrawDebitCredit");
            this.debitCreditCardService.refreshBalance();

            this.debitAndCreditCardForm.reset();
          }
          else if (response.error.Message)
            this.showAlertMessage('Withdraw Error', response.error.Message);
          else
            this.showAlertMessage('Withdraw Error', response.error.Error);

          this.isWithDrawing = false;
          this.cdRef.markForCheck();
        }

        this.subsWithdrawCardResponse?.unsubscribe();

        if (isSuccess) {
          this.showAlertMessage('Successful Withdrawal', 'Successfully Withdraw Amount');
          this.debitAndCreditCardForm.reset();
          this.isWithDrawing = false;
          this.cdRef.markForCheck();
        }
      });
    }
    else {
      // OLD (moved to WithdrawDebitCreditCardService.markFormGroupTouched):
      // this.fundingService.markFormGroupTouched(this.debitAndCreditCardForm);
      this.debitCreditCardService.markFormGroupTouched(this.debitAndCreditCardForm);
    }
  }

  checkConnectivity(): void {
    // OLD (moved to WithdrawDebitCreditCardService.checkConnectivity):
    // this.fundingService.checkConnectivity(
    //   this.environment,
    //   'DebitCreditCardComponent:checkConnectivity',
    //   (online: boolean) => this.isOnline = online
    // );
    this.debitCreditCardService.checkConnectivity(
      'DebitCreditCardComponent:checkConnectivity',
      (online: boolean) => this.isOnline = online
    );
  }

}
